/**
 * 現在のユーザー情報を管理するフック
 */

import { useState, useEffect } from 'react'
import apiClient from '@/lib/api/client'
import logger from '@/utils/logger'

/**
 * 現在のユーザー情報
 */
export interface CurrentUser {
    /** ユーザーID */
    id: string
    /** メールアドレス */
    email: string
    /** ユーザーロール */
    role: string
    /** 所属会社ID（テナント境界。realtime 購読トピックのスコープに使用） */
    company_id?: string | null
    /** 作成日時 */
    created_at: string
    /** 更新日時 */
    updated_at: string
}

/**
 * 現在のユーザー情報を取得・管理するフック
 */
export const useCurrentUser = () => {
    const [user, setUser] = useState<CurrentUser | null>(null)
    const [loading, setLoading] = useState(true)
    const [error, setError] = useState<string | null>(null)

    useEffect(() => {
        const fetchCurrentUser = async () => {
            try {
                setLoading(true)
                setError(null)

                const response = await apiClient.get<CurrentUser>('/users/me')
                setUser(response.data)
            } catch (err: any) {
                // 401 は「未ログイン状態」であって不具合ではない。auth context が login 遷移を担う。
                // ApiClientError (err.status) / AxiosError (err.response.status) 両形に対応。
                const status = err?.status ?? err?.response?.status
                if (status === 401) {
                    setUser(null)
                    setError(null)
                } else {
                    logger.error('Failed to fetch current user', err, {
                        errorMessage: err.response?.data?.message,
                    })
                    setError(err.response?.data?.message || 'ユーザー情報の取得に失敗しました')
                    setUser(null)
                }
            } finally {
                setLoading(false)
            }
        }

        fetchCurrentUser()
    }, [])

    return {
        user,
        loading,
        error,
        refetch: () => {
            setLoading(true)
            setError(null)
        }
    }
}
